import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from '@mui/styles'
import PropTypes from 'prop-types'
import clsx from 'clsx'

import lang from '@/lang'

import { TextInput, CountryInput } from '@/modules/base/components/inputs'
import { setCompanyData } from '@/redux/ducks/onboarding/main-v2'
import { DEFAULT_EMPTY_ADDRESS_OBJECT } from '@/modules/base/util/constants'
import { isMeinBuero } from '@/utils/build-env'
import validateCompanyName from '@/modules/onboardingV2/helpers/meinbuero/validateCompanyName'
import InputNames from '@/modules/onboardingV2/constants/input-names'

const { taxConsultantCompanyAddress: { inputs: inputLabels } } = lang.onboardingV2

const useStyles = makeStyles(theme => ({
  root: () => ({
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-between',
  }),
  column: {
    width: '100%',
  },
  inputsRow: {
    marginBottom: 20,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    '@media (min-width: 800px)': {
      flexDirection: 'row',
    },
  },
  countryInput: {
    width: '100%',
    zIndex: 50,
  },
  zip: {
    '@media (min-width: 800px)': {
      flexBasis: '25%',
      paddingRight: 20,
    },
  },
}))

const TaxConsultantAddressInputs = props => {
  const classes = useStyles()
  const { setInputsValid } = props
  const dispatch = useDispatch()
  const companyData = useSelector(state => state.onboarding.mainV2.companyData)
  const [errorMessages, setErrorMessages] = useState({ companyName: '' })

  const validateInput = (name, value) => {
    if (!isMeinBuero()) { return }

    const updatedErrorMessage = { ...errorMessages }
    switch (name) {
      case InputNames.companyName: {
        const isValid = validateCompanyName(value)
        updatedErrorMessage.companyName = isValid ? '' : lang.onboardingV2.taxConsultantCompanyAddress.errors.taxOffice
        break
      }
    }

    setErrorMessages(updatedErrorMessage)
  }

  const handleInputChange = name => value => {
    validateInput(name, value)
    dispatch(setCompanyData({ ...companyData, [name]: value }))
  }

  useEffect(() => {
    const {
      companyName,
    } = companyData
    if (isMeinBuero()) {
      setInputsValid(!errorMessages.companyName && companyName)
      return
    }

    setInputsValid(companyName)
  }, [companyData])

  return (
    <>
      <div className={classes.root}>
        <div className={classes.column}>
          <div className={classes.inputsRow}>
            <TextInput
              required
              name={InputNames.companyName}
              label={inputLabels.companyName}
              value={companyData.companyName}
              errorMessage={errorMessages.companyName}
              onChange={handleInputChange(InputNames.companyName)}
            />
          </div>
        </div>
      </div>
      <div className={classes.root}>
        <div className={classes.column}>
          <div className={classes.inputsRow}>
            <TextInput
              className={classes.textField}
              name={InputNames.street}
              label={inputLabels.street}
              value={companyData.street}
              onChange={handleInputChange(InputNames.street)}
            />
          </div>
        </div>
      </div>
      <div className={classes.root}>
        <div className={classes.column}>
          <div className={classes.inputsRow}>
            <TextInput
              className={classes.zip}
              name={InputNames.zipCode}
              label={inputLabels.zipCode}
              value={companyData.zipCode}
              onChange={handleInputChange(InputNames.zipCode)}
            />
            <TextInput
              className={classes.city}
              name={InputNames.city}
              label={inputLabels.city}
              value={companyData.city}
              onChange={handleInputChange(InputNames.city)}
            />
          </div>
        </div>
      </div>
      <div className={classes.root}>
        <div className={classes.column}>
          <div className={classes.inputsRow}>
            <CountryInput value={DEFAULT_EMPTY_ADDRESS_OBJECT.countryIso} includeCodeInLabel className={clsx(classes.countryInput)} />
          </div>
        </div>
      </div>
    </>
  )
}

TaxConsultantAddressInputs.propTypes = {
  setInputsValid: PropTypes.func,
}

TaxConsultantAddressInputs.defaultProps = {
  setInputsValid: () => {},
}

export default TaxConsultantAddressInputs
